// Promote… (spec/dashboard.md section 12.2; R-BR-04; R-ACC-01 protected environments): ships what an environment runs to
// the next one in dev -> stage -> prod as that environment's default pin (`POST …/envs/{target}/pins { releaseId, reason }`),
// so promoting never means hunting for the same release in a select. The dialog names both releases, shows the
// field-level Changes the target would take (ReleaseDiff from the target's current commit to the source's), needs a
// reason on a protected target and the typed target slug there.
import { useState } from 'react';
import { ops } from '../../api/operations';
import type { Environment, Release } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { useGame } from '../../shell/GameContext';
import { Button } from '../../ui/Button';
import { Field } from '../../ui/Field';
import { TextInput } from '../../ui/inputs';
import { Modal } from '../../ui/Modal';
import { formatInstant, useTimeZone } from '../../ui/time';
import { useToasts } from '../../ui/Toast';
import { ChangesSection, ServerError, TypedConfirm, useActivePins } from './PinDialogs';
import { activeDefaultPin, agoText, asApiError, currentRelease, releaseName } from './releases';
import '../org/org.css';

export function PromoteDialog({
  source,
  target,
  releases,
  open,
  onClose,
  onDone,
  missingPermission,
}: {
  source: Environment;
  target: Environment;
  releases: Release[];
  open: boolean;
  onClose: () => void;
  onDone: () => void;
  missingPermission: string | null;
}) {
  const { org, game, offsets } = useGame();
  const { client } = useAuth();
  const toasts = useToasts();
  const [zone] = useTimeZone();
  const sourcePins = useActivePins(source.slug);
  const targetPins = useActivePins(target.slug);
  const sourcePin = activeDefaultPin(sourcePins.data?.items);
  const sourceRelease = currentRelease(sourcePins.data?.items, releases);
  const targetPin = activeDefaultPin(targetPins.data?.items);
  const targetRelease = currentRelease(targetPins.data?.items, releases);
  const [reason, setReason] = useState('');
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = () => {
    setReason('');
    setTyped('');
    setError(null);
    onClose();
  };

  const releaseId = sourcePin?.releaseId ?? null;
  const name = releaseId ? releaseName(releaseId, releases) : '';
  const reasonMissing = target.isProtected && !reason.trim();
  const unconfirmed = target.isProtected && typed !== target.slug;
  const submit = async () => {
    if (!releaseId || reasonMissing || unconfirmed) return;
    setBusy(true);
    setError(null);
    try {
      await client.data(ops.pinRelease, { path: { org, game: game.slug, env: target.slug }, body: { releaseId, ...(reason.trim() ? { reason: reason.trim() } : {}) } });
      toasts.success(`Promoted ${name} to ${target.name}`);
      onDone();
      close();
    } catch (e) {
      setError(asApiError(e).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Promote to ${target.name}`} open={open} onClose={close} size="m">
      <form
        data-testid="promote-dialog"
        className="pin-dialog"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <p className="small" data-testid="promote-summary">
          {releaseId ? (
            <>
              Promote <strong>{name}</strong>
              {sourceRelease ? ` (branch ${sourceRelease.branchName}, built ${formatInstant(sourceRelease.createdAt, zone, { offsets })} by ${sourceRelease.builtBy?.label ?? '—'})` : ''} from {source.name} to {target.name}.{' '}
            </>
          ) : sourcePins.data ? (
            <>Nothing is pinned on {source.name}: there is nothing to promote. </>
          ) : (
            <>Reading what {source.name} runs… </>
          )}
          {targetPin ? (
            <>
              {target.name} runs <strong>{releaseName(targetPin.releaseId, releases)}</strong> ({targetRelease ? `branch ${targetRelease.branchName}, ` : ''}pinned {agoText(targetPin.createdAt)}).
            </>
          ) : targetPins.data ? (
            <>{target.name} runs nothing yet.</>
          ) : null}
        </p>
        {targetPin && sourcePin && targetPin.releaseId === sourcePin.releaseId && <p className="muted small">{target.name} already runs this release.</p>}
        <ChangesSection from={targetRelease} to={sourceRelease} defaultOpen />
        <Field label="Reason" required={target.isProtected}>
          <TextInput aria-label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        {target.isProtected && <TypedConfirm env={target} value={typed} onChange={setTyped} why={`${target.name} is protected: pins here reach players at once.`} />}
        <ServerError message={error} />
        <div className="modal-footer form-footer">
          <Button onClick={close}>Cancel</Button>
          <Button
            type="submit"
            variant="primary"
            busy={busy}
            disabled={!releaseId || reasonMissing || unconfirmed}
            missingPermission={missingPermission}
            title={!releaseId ? `Nothing is pinned on ${source.name}` : reasonMissing ? 'Give a reason' : unconfirmed ? `Type ${target.slug} to confirm` : undefined}
          >
            Promote
          </Button>
        </div>
      </form>
    </Modal>
  );
}
